import React from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem } from '@fluentui/react-components';
import RaciLegendPopover from './RaciLegendPopover';
import RaciMatrixTable, { activityHasAssignments, columnHasAssignments } from './RaciMatrixTable';
import { useRaciMatrix } from './useRaciMatrix';
import { getAccountableWarnings, isRaciMatrixEmpty } from './raciMatrixModel';
import styles from './css/index.module.css';

const RaciMatrix = () => {
    const { selectedProject } = useSelector(state => state.projectData);
    const projectId = selectedProject?.id || localStorage.getItem('project_id');

    const {
        matrix,
        saveStatus,
        initialLoading,
        loadError,
        canEdit,
        retrySave,
        reload,
        setAssignment,
        addRow,
        addGroup,
        addColumn,
        renameActivity,
        deleteActivity,
        moveActivityUp,
        moveActivityDown,
        moveActivityToGroup,
        renameColumn,
        deleteColumn,
        moveColumnLeft,
        moveColumnRight,
        renameGroup,
        deleteGroup,
        moveGroupUp,
        moveGroupDown,
    } = useRaciMatrix(projectId);

    const readOnly = !canEdit;

    const [collapsedGroupIds, setCollapsedGroupIds] = React.useState(() => new Set());

    const toggleGroupCollapsed = (groupId, collapsed) => {
        setCollapsedGroupIds(prev => {
            const next = new Set(prev);
            if (collapsed) next.add(groupId);
            else next.delete(groupId);
            return next;
        });
    };

    const collapseAllGroups = () => {
        setCollapsedGroupIds(new Set(matrix.groups.map(g => g.id)));
    };

    const expandAllGroups = () => {
        setCollapsedGroupIds(new Set());
    };

    const [editingActivityId, setEditingActivityId] = React.useState(null);
    const [focusedCell, setFocusedCell] = React.useState(null);
    const [addingColumn, setAddingColumn] = React.useState(false);
    const [newColumnLabel, setNewColumnLabel] = React.useState('');
    const [groupModalOpen, setGroupModalOpen] = React.useState(false);
    const [groupNameDraft, setGroupNameDraft] = React.useState('');
    const [renameGroupId, setRenameGroupId] = React.useState(null);
    const [renameColumnId, setRenameColumnId] = React.useState(null);
    const [renameColumnDraft, setRenameColumnDraft] = React.useState('');
    const [pendingDelete, setPendingDelete] = React.useState(null);

    const warnings = React.useMemo(() => getAccountableWarnings(matrix), [matrix]);
    const matrixIsEmpty = isRaciMatrixEmpty(matrix);

    const handleAddRow = () => {
        const id = addRow(null);
        if (id) setEditingActivityId(id);
    };

    const handleCommitNewColumn = () => {
        const label = newColumnLabel.trim();
        if (label) addColumn(label);
        setNewColumnLabel('');
        setAddingColumn(false);
    };

    const openGroupModal = () => {
        setGroupNameDraft('');
        setRenameGroupId(null);
        setGroupModalOpen(true);
    };

    const submitGroupModal = () => {
        const name = groupNameDraft.trim();
        if (!name) return;
        if (renameGroupId) {
            renameGroup(renameGroupId, name);
        } else {
            addGroup(name);
        }
        setGroupModalOpen(false);
        setGroupNameDraft('');
        setRenameGroupId(null);
    };

    const groupMenuAction = {
        moveActivityToGroup,
        moveActivityUp,
        moveActivityDown,
        startRenameGroup: groupId => {
            const group = matrix.groups.find(g => g.id === groupId);
            setRenameGroupId(groupId);
            setGroupNameDraft(group?.name || '');
            setGroupModalOpen(true);
        },
        moveGroupUp,
        moveGroupDown,
        deleteGroup: groupId => setPendingDelete({ type: 'group', id: groupId }),
        addActivityInGroupWithName: (groupId, name) => {
            const id = addRow(groupId);
            if (id && name) renameActivity(id, name);
        },
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        if (pendingDelete.type === 'activity') deleteActivity(pendingDelete.id);
        if (pendingDelete.type === 'column') deleteColumn(pendingDelete.id);
        if (pendingDelete.type === 'group') deleteGroup(pendingDelete.id);
        setPendingDelete(null);
    };

    if (initialLoading) {
        return (
            <div className={styles.page}>
                <p className={styles.subtitle}>Loading RACI Matrix…</p>
            </div>
        );
    }

    if (loadError && !initialLoading) {
        return (
            <div className={styles.page}>
                <p className={styles.saveStatusError}>Could not load RACI Matrix.</p>
                <button type="button" className={styles.retryButton} onClick={reload}>
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div>
                    <div className={styles.titleRow}>
                        <h1 className={styles.title}>RACI Matrix</h1>
                        <RaciLegendPopover />
                    </div>
                    <p className={styles.subtitle}>
                        Define responsibilities and involvement across project activities and deliverables.
                    </p>
                </div>
                <div className={styles.saveRow}>
                    {saveStatus === 'error' && (
                        <>
                            <span className={`${styles.saveStatus} ${styles.saveStatusError}`}>
                                Changes could not be saved.
                            </span>
                            <button type="button" className={styles.retryButton} onClick={retrySave}>
                                Retry
                            </button>
                        </>
                    )}
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <button type="button" className={styles.moreMenuButton} aria-label="More RACI Matrix actions">
                                ···
                            </button>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem onClick={collapseAllGroups}>Collapse all groups</MenuItem>
                                <MenuItem onClick={expandAllGroups}>Expand all groups</MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>
            </div>

            {readOnly && (
                <div className={styles.readOnlyBanner}>You have view-only access to this RACI Matrix. Editing is disabled.</div>
            )}

            {!readOnly && warnings.length > 0 && (
                <div className={styles.warnings} role="status">
                    Advisory: review Accountable assignments.
                    <ul>
                        {warnings.slice(0, 3).map(w => (
                            <li key={w}>{w}</li>
                        ))}
                        {warnings.length > 3 && <li>…and {warnings.length - 3} more</li>}
                    </ul>
                </div>
            )}

            <div className={styles.toolbar}>
                <button type="button" className={styles.btnPrimary} disabled={readOnly} onClick={handleAddRow}>
                    + Add activity
                </button>
                <button type="button" className={styles.btnSecondary} disabled={readOnly} onClick={openGroupModal}>
                    + Add group
                </button>
                <button
                    type="button"
                    className={styles.btnSecondary}
                    disabled={readOnly}
                    onClick={() => {
                        setAddingColumn(true);
                        setNewColumnLabel('');
                    }}>
                    + Add column
                </button>
            </div>

            <div className={styles.matrixCard}>
                <RaciMatrixTable
                    matrix={matrix}
                    readOnly={readOnly}
                    addingColumn={addingColumn}
                    newColumnLabel={newColumnLabel}
                    onNewColumnLabelChange={setNewColumnLabel}
                    onCommitNewColumn={handleCommitNewColumn}
                    onCancelNewColumn={() => {
                        setAddingColumn(false);
                        setNewColumnLabel('');
                    }}
                    focusedCell={focusedCell}
                    onFocusCell={(activityId, columnId) => setFocusedCell({ activityId, columnId })}
                    onBlurCell={() => setFocusedCell(null)}
                    onAssignmentChange={setAssignment}
                    editingActivityId={editingActivityId}
                    onStartEditActivity={setEditingActivityId}
                    onRenameActivity={renameActivity}
                    onEndEditActivity={() => setEditingActivityId(null)}
                    onCancelEditActivity={() => setEditingActivityId(null)}
                    onRequestDeleteActivity={activity => {
                        if (activityHasAssignments(matrix, activity.id)) {
                            setPendingDelete({ type: 'activity', id: activity.id, label: activity.name });
                        } else {
                            deleteActivity(activity.id);
                        }
                    }}
                    onRequestDeleteColumn={col => {
                        if (columnHasAssignments(matrix, col.id)) {
                            setPendingDelete({ type: 'column', id: col.id, label: col.label });
                        } else {
                            deleteColumn(col.id);
                        }
                    }}
                    onRenameColumn={columnId => {
                        const col = matrix.columns.find(c => c.id === columnId);
                        setRenameColumnId(columnId);
                        setRenameColumnDraft(col?.label || '');
                    }}
                    onMoveColumnLeft={moveColumnLeft}
                    onMoveColumnRight={moveColumnRight}
                    collapsedGroupIds={collapsedGroupIds}
                    onToggleGroupCollapsed={toggleGroupCollapsed}
                    onAddActivityInGroup={groupId => {
                        const id = addRow(groupId);
                        if (id) setEditingActivityId(id);
                    }}
                    onGroupMenuAction={groupMenuAction}
                    onAddActivity={handleAddRow}
                    onStartAddColumn={() => {
                        setAddingColumn(true);
                        setNewColumnLabel('');
                    }}
                />
            </div>

            {matrixIsEmpty ? (
                <p className={styles.matrixFootnote}>
                    Rows are activities or deliverables. Columns are roles, teams or stakeholders.
                </p>
            ) : (
                <p className={styles.tip}>Tip: focus a cell and press R, A, C or I to assign. Use Delete or Backspace to clear.</p>
            )}

            <Modal show={groupModalOpen} onHide={() => setGroupModalOpen(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{renameGroupId ? 'Rename group' : 'Add group'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <label htmlFor="raci-group-name">Group name</label>
                    <input
                        id="raci-group-name"
                        className={styles.modalField}
                        value={groupNameDraft}
                        onChange={e => setGroupNameDraft(e.target.value)}
                        placeholder="e.g. Phase 1 — Discovery"
                        onKeyDown={e => {
                            if (e.key === 'Enter') submitGroupModal();
                        }}
                        autoFocus
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className={styles.btnSecondary} onClick={() => setGroupModalOpen(false)}>
                        Cancel
                    </button>
                    <button type="button" className={styles.btnPrimary} onClick={submitGroupModal}>
                        {renameGroupId ? 'Save' : 'Add group'}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={Boolean(renameColumnId)} onHide={() => setRenameColumnId(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Rename column</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <label htmlFor="raci-column-name">Column label</label>
                    <input
                        id="raci-column-name"
                        className={styles.modalField}
                        value={renameColumnDraft}
                        onChange={e => setRenameColumnDraft(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                renameColumn(renameColumnId, renameColumnDraft.trim());
                                setRenameColumnId(null);
                            }
                        }}
                        autoFocus
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className={styles.btnSecondary} onClick={() => setRenameColumnId(null)}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.btnPrimary}
                        onClick={() => {
                            renameColumn(renameColumnId, renameColumnDraft.trim());
                            setRenameColumnId(null);
                        }}>
                        Save
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={Boolean(pendingDelete)} onHide={() => setPendingDelete(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {pendingDelete?.type === 'activity' &&
                        `Delete "${pendingDelete.label || 'this activity'}" and all RACI assignments in its row?`}
                    {pendingDelete?.type === 'column' &&
                        `Delete "${pendingDelete.label || 'this column'}" and all RACI assignments in that column?`}
                    {pendingDelete?.type === 'group' &&
                        'Delete this group? Activities will move to Ungrouped and keep their assignments.'}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className={styles.btnSecondary} onClick={() => setPendingDelete(null)}>
                        Cancel
                    </button>
                    <button type="button" className={styles.btnPrimary} onClick={confirmDelete}>
                        Delete
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default RaciMatrix;
